/**
 * Work profiles — UX presets, NOT security roles.
 *
 * A profile is a convenient starting arrangement of the navigation rail and a
 * default landing page for a way of working (support, engineering, …). It only
 * ever decides which *permitted* items are surfaced and where the user lands;
 * it never grants access. A user on the "admin" work profile who lacks a
 * permission still sees nothing they aren't allowed to — the resolver filters
 * by permission first, always.
 */
import { NAV_ITEM_IDS } from "./nav-catalog";
import type { NavPresence, NavPresenceSignal } from "./presence";

export const WORK_PROFILES = [
  "starter",
  "support",
  "engineering",
  "release_devops",
  "manager_reviewer",
  "admin",
  "custom",
] as const;
export type WorkProfile = (typeof WORK_PROFILES)[number];

export const DEFAULT_WORK_PROFILE: WorkProfile = "custom";

/**
 * What a *newly created* account starts on. Deliberately NOT
 * `DEFAULT_WORK_PROFILE`: that one is the fallback for every row that has no
 * stored profile, which includes every account that predates this field —
 * changing it would silently shrink the rail for existing users. New users
 * get the small starter rail; everyone else keeps what they had.
 */
export const NEW_USER_WORK_PROFILE: WorkProfile = "starter";

export function isWorkProfile(value: unknown): value is WorkProfile {
  return typeof value === "string" && (WORK_PROFILES as readonly string[]).includes(value);
}

export type WorkProfilePreset = {
  id: WorkProfile;
  /** Default landing nav-item id after sign-in. */
  startPage: string;
  /**
   * Nav-item ids this profile surfaces, or "all" to surface every permitted
   * item. Items not listed are hidden by the preset (a user can re-pin them in
   * Custom, and locked/always-visible items still show). "all" means the
   * profile hides nothing on its own.
   */
  nav: readonly string[] | "all";
};

/**
 * Presets. Ids reference the catalog; the resolver ignores any unknown id, so
 * these stay forgiving if the catalog evolves. `custom` and `admin` surface
 * everything — `custom` then defers entirely to the user's pin/hide choices.
 */
export const WORK_PROFILE_PRESETS: Record<WorkProfile, WorkProfilePreset> = {
  // The first-week rail: where work is created and what came back, nothing
  // else. The AI-results rows (missions, workspaces, patches, sandbox runs)
  // are NOT listed — they arrive through GROWN_NAV_ITEMS once the tenant has
  // actually produced one, so a brand-new workspace isn't asked to navigate
  // four empty pipeline pages on day one.
  starter: {
    id: "starter",
    startPage: "dashboard",
    nav: ["dashboard", "inbox", "notifications", "projects", "issues", "followUps", "knowledge", "settings"],
  },
  support: {
    id: "support",
    startPage: "inbox",
    // "intake" was in this list but has never existed in the catalog — the
    // resolver ignored it and the dev guard below warned about it on boot.
    nav: ["dashboard", "inbox", "notifications", "issues", "customerDirectory", "knowledge", "projects", "followUps", "settings"],
  },
  engineering: {
    id: "engineering",
    startPage: "issues",
    // `notifications` is not optional for a profile that answers approval
    // gates: without it the unread badge (and the only place the gate queue
    // is listed) was unreachable from the rail.
    nav: ["notifications", "projects", "issues", "followUps", "missions", "workspaces", "patches", "sandboxRuns", "settings"],
  },
  release_devops: {
    id: "release_devops",
    startPage: "missions",
    nav: ["notifications", "projects", "missions", "patches", "sandboxRuns", "governance", "settings"],
  },
  manager_reviewer: {
    id: "manager_reviewer",
    startPage: "dashboard",
    // A reviewer approves patches, so the patch list belongs in their rail —
    // it was the one surface this profile was missing.
    nav: ["dashboard", "notifications", "issues", "customerDirectory", "missions", "patches", "governance", "settings"],
  },
  admin: {
    id: "admin",
    startPage: "dashboard",
    nav: "all",
  },
  custom: {
    id: "custom",
    startPage: "dashboard",
    nav: "all",
  },
};

export function workProfilePreset(profile: WorkProfile): WorkProfilePreset {
  return WORK_PROFILE_PRESETS[profile] ?? WORK_PROFILE_PRESETS[DEFAULT_WORK_PROFILE];
}

/**
 * Progressive disclosure for the starter rail.
 *
 * Key = nav item id, value = the tenant signal that makes it appear. A row
 * listed here is hidden by the starter preset until the tenant has produced
 * the thing the row lists, and it is never hidden again once it has (the
 * signals only ever go from false to true, and a user can pin any row
 * manually regardless).
 *
 * Only the starter profile grows — every other preset is an explicit choice
 * by (or for) someone who already knows the product.
 */
export const GROWN_NAV_ITEMS: Readonly<Record<string, NavPresenceSignal>> = {
  missions: "missions",
  workspaces: "workspaces",
  patches: "patches",
  sandboxRuns: "sandboxRuns",
};

/** Profiles whose preset grows with `GROWN_NAV_ITEMS`. */
export const GROWING_PROFILES: ReadonlySet<WorkProfile> = new Set<WorkProfile>(["starter"]);

/**
 * Whether a profile's preset surfaces a nav item *before* any user pin/hide.
 * Used by the settings checklist to translate a checkbox toggle into the right
 * pin/hide delta (see the NavPreferencesProvider).
 */
export function profileIncludesNavItem(
  profile: WorkProfile,
  id: string,
  presence?: NavPresence,
): boolean {
  const preset = workProfilePreset(profile);
  if (preset.nav === "all") return true;
  if (preset.nav.includes(id)) return true;
  const signal = GROWING_PROFILES.has(profile) ? GROWN_NAV_ITEMS[id] : undefined;
  return signal !== undefined && presence?.[signal] === true;
}

// Defensive: keep the presets honest against the catalog at module load in dev.
// A typo'd id silently dropping out of a preset would be a confusing bug.
if (process.env.NODE_ENV !== "production") {
  const known = new Set(NAV_ITEM_IDS);
  for (const preset of Object.values(WORK_PROFILE_PRESETS)) {
    if (preset.nav === "all") continue;
    for (const id of preset.nav) {
      if (!known.has(id)) {
        // eslint-disable-next-line no-console
        console.warn(`[work-profiles] preset "${preset.id}" references unknown nav id "${id}"`);
      }
    }
  }
}
